import { useMemo, useState, useRef, useEffect } from 'react'
import { Table } from 'antd'
import { useSpring, animated } from 'react-spring'
import { TranslationKeys } from '../../Constants/TranslationKeys'
import {
  renderTranslation,
  handleMultipleTableScroll,
  getCurrencyConversion,
  getCurrencySymbolByPricelist,
  handleMultipleTableHover,
  handleMultipleLeaveTableHover,
} from '../../Helpers/GenericHelpers'
import {
  PriceTypeShow,
  priceStandardCellDiscountWidth,
  priceStandardCellPriceStoresWidth,
  priceStandardCellPriceWidth,
  priceStandardHeight,
  priceStandardRowHeight,
} from '../../Constants/TableConfiguration'
import PricesListingHeader from './PricesListingHeader'
import { useAppSelector } from '../../hooks'
import {
  getPricelistByName,
  getBasePriceForStore,
  getListingBySize,
  getListingVat,
  getPriceDataByType,
  getRowInnerElementsCount,
  hasDiscountStoresRows,
} from '../../Helpers/PricesHelper'
import { DateFormatNoYear, RuleType } from 'Constants/Configuration'
import dayjs from 'dayjs'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { isValidDate } from 'Helpers/DateHelpers'

function Prices(props) {
  const language = useAppSelector(state => state.present.language.value)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const priceData = useAppSelector(state => state.present.articleDetail.articleSelected.priceData)
  const priceTypeFilter = useAppSelector(state => state.present.price.priceTypeFilter)
  const isListingTypeSell = props.activeListing ? priceData.find(x => x.name === props.activeListing)?.isSell : false
  const priceList1 = useAppSelector(state => state.present.tableOptions.priceList1)
  const channelType: TypeOfChannel = useAppSelector(state => state.present.price.channelType)
  const allPricelists = useAppSelector(state => state.present.configuration.allPricelists)
  const [isColumnOpen, setIsColumnOpen] = useState(true)
  const [isColumnMinified, setIsColumnMinified] = useState(false)
  const [componentWidth, setComponentWidth] = useState(0)
  const priceListingTableRef = useRef<any>(undefined)
  const currentPricelist = getPricelistByName(priceData, props.activeListing)

  const [filteredData, isBySize, hasVat] = useMemo(() => {
    return [
      getPriceDataByType(priceData, priceTypeFilter).filter(priceData => {
        return priceData.name === props.activeListing
      }),
      getListingBySize(priceData),
      getListingVat(priceData, props.activeListing),
    ]
  }, [priceData, priceTypeFilter, props.activeListing])

  const listingPriceMappedData = useMemo(() => {
    if (priceData && priceData.length > 0 && props.activeListing) {
      const priceDataToReturn = []
      props.distinctPriceDates.forEach(rowData => {
        if (isBySize && !rowData.size) {
          priceDataToReturn.push({ discount: 'x', price: 'x', isHidden: true, key: rowData.startDate, isDateRow: true })
        }

        filteredData.forEach(priceData => {
          if (rowData.listings.indexOf(priceData.name) !== -1) {
            priceData.prices.forEach(price => {
              if (rowData.startDate === price.startDate && (isBySize ? rowData.size === price.size : !isBySize)) {
                priceDataToReturn.push(
                  Object.assign({ key: price.startDate + (rowData.size ? '-' + rowData.size : '') }, price),
                )
              }
            })
          } else {
            const priceBeforeDate = priceData.prices.filter(
              x => Date.parse(x.startDate) < Date.parse(rowData.startDate),
            )
            const priceDateElementSort = priceBeforeDate.sort(
              (a, b) => Date.parse(a.startDate) < Date.parse(b.startDate),
            )

            const key = rowData.startDate + (rowData.size ? '-' + rowData.size : '')

            if (priceDateElementSort.length > 0) {
              priceDataToReturn.push(Object.assign({ key: key }, priceDateElementSort[priceDateElementSort.length - 1]))
            } else {
              priceDataToReturn.push({ discount: 'x', price: 'x', isHidden: true, key: key })
            }
          }
        })
      })
      return priceDataToReturn
    }

    return []
  }, [priceData, props.activeListing, props.distinctPriceDates, filteredData, isBySize])

  const cellPriceWidth = useMemo(() => {
    return channelType == TypeOfChannel.Store && hasDiscountStoresRows(priceData)
      ? priceStandardCellPriceStoresWidth
      : priceStandardCellPriceWidth
  }, [channelType, priceData])

  const getSalesDate = startDate => {
    if (isValidDate(startDate)) {
      return dayjs(startDate).format(DateFormatNoYear)
    } else return ''
  }
  const listingChildren = useMemo(() => {
    return [
      {
        title: (
          <div className='line-height-32 text-right pr-1' style={{ width: priceStandardCellDiscountWidth }}>
            {isListingTypeSell ? renderTranslation(language, TranslationKeys.priceSectionTableDiscountLabel) : 'RI'}
          </div>
        ),
        dataIndex: 'discount',
        key: 'discount',
        className: 'listing-discount' + (isListingTypeSell ? '' : ' sale'),
        width: priceStandardCellDiscountWidth,
        render: (text, record) => {
          return <CellDiscount text={text} record={record} />
        },
      },
      {
        title: (
          <div className='line-height-32 text-right pr-1' style={{ width: cellPriceWidth }}>
            <span className='pr-1'>{getCurrencySymbolByPricelist(priceList1, props.activeListing)}</span>
            <span>
              {`(${renderTranslation(language, TranslationKeys.listingPopListingVat)}: ${
                hasVat
                  ? renderTranslation(language, TranslationKeys.listingPopListingVatYes)
                  : renderTranslation(language, TranslationKeys.listingPopListingVatNo)
              })`}
            </span>
          </div>
        ),
        dataIndex: 'price',
        key: 'price',
        className: 'listing-price',
        width: cellPriceWidth,
        render: (text, record) => {
          return <CellPrice text={text} record={record} />
        },
      },
    ]
  }, [
    hasVat,
    isBySize,
    isListingTypeSell,
    language,
    priceList1,
    props.activeListing,
    props.priceTypeShow,
    cellPriceWidth,
  ])

  const CellDiscount = props => {
    const rowsCount = getRowInnerElementsCount(priceData, props.record.startDate)

    return props.record.storesDiscounts && props.record.storesDiscounts.length > 0 ? (
      <div style={{ height: priceStandardRowHeight * rowsCount }}>
        {props.record.storesDiscounts.map((e, i) => (
          <div
            key={i}
            className={
              (props.record.isCurrent ? 'bold' : '') +
              (props.record.type && props.record.type === RuleType.salesPeriod && Number(e.discountValue) > 0
                ? ' salesperiod'
                : '') +
              (props.record.isHidden && isBySize && PriceTypeShow.history === props.priceTypeShow
                ? 'header-price-date-history'
                : '')
            }
            style={{
              visibility: props.record.isHidden ? 'hidden' : 'visible',
              borderBottom: '1px solid #f0f0f0',
              height: priceStandardRowHeight,
              alignContent: 'center',
            }}
          >
            {e.discountValue}
          </div>
        ))}
      </div>
    ) : (
      <div
        className={
          (props.record.isCurrent ? 'bold' : '') +
          (props.record.type && props.record.type === RuleType.salesPeriod
            ? ' salesperiod'
            : props.record.type && props.record.type === RuleType.promo
            ? ' promo'
            : '') +
          (props.record.isHidden && isBySize && PriceTypeShow.history === props.priceTypeShow
            ? 'header-price-date-history'
            : '')
        }
        style={{ visibility: props.record.isHidden ? 'hidden' : 'visible', height: priceStandardRowHeight }}
      >
        {props.text}
      </div>
    )
  }
  const CellPrice = props => {
    const rowsCount = getRowInnerElementsCount(priceData, props.record.startDate)
    return rowsCount > 1 ? (
      <div style={{ height: priceStandardRowHeight * rowsCount, maxHeight: priceStandardRowHeight * rowsCount }}>
        {props.record.storesDiscounts.map((e, i) => (
          <div className='d-flex flex-row flex-nowrap border-table' key={i}>
            <div className='flex-column price-cell'>
              {getSalesDate(e.startDate)} {e.name}
            </div>

            <div
              className={`flex-column ${props.record.isCurrent ? 'bold' : ''}`}
              style={{
                visibility: props.record.isHidden ? 'hidden' : 'visible',
                width: '40%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {getCurrencyConversion(e.price)}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div
        className={props.record.isCurrent ? 'bold' : ''}
        style={{ visibility: props.record.isHidden ? 'hidden' : 'visible', height: priceStandardRowHeight }}
      >
        {getCurrencyConversion(props.text)}
      </div>
    )
  }

  const basePrice = useMemo(() => {
    if (channelType != TypeOfChannel.Store) {
      return null
    }
    return getBasePriceForStore(priceData, props.activeListing)
  }, [channelType, priceData])

  const invariantInfo = useMemo(() => {
    if (channelType != TypeOfChannel.Store) {
      return ''
    }
    if (currentPricelist.isBaseInvariant) {
      if (currentPricelist.isBySize) {
        const baseForSize = allPricelists.filter(x => x.isBaseForSize).map(pricelist => pricelist.name)
        return ` (${baseForSize}) `
      }
      return ' (M/V) '
      // } else {
      //   return `( ${
      //     currentPricelist.isSell ? currentPricelist.prices[0]?.sellPrice : currentPricelist.prices[0]?.buyPrice
      //   }€ )`
    }
    return ''
  }, [channelType, priceData])

  const listingTableColumns = useMemo(() => {
    return [
      {
        title: (
          <PricesListingHeader
            {...props}
            showPrice={channelType === TypeOfChannel.Store}
            basePrice={basePrice}
            isBySize={currentPricelist.isBySize}
            invariantInfo={invariantInfo}
            isColumnOpen={isColumnOpen}
            isColumnMinified={isColumnMinified}
            setIsColumnOpen={setIsColumnOpen}
            language={language}
          />
        ),
        children: listingChildren,
      },
    ]
  }, [props, isColumnOpen, isColumnMinified, listingChildren, channelType])

  useEffect(() => {
    const tableBody =
      priceListingTableRef &&
      priceListingTableRef.current &&
      priceListingTableRef.current.querySelector('.ant-table-body')
    tableBody && tableBody.addEventListener('scroll', handleMultipleTableScroll, { passive: true })
    const rows = tableBody && tableBody.querySelectorAll('tr.ant-table-row')
    let i = 0
    rows.forEach(row => {
      row.classList.add(`sameRow-${i}`)
      i++
      row.addEventListener('mouseover', handleMultipleTableHover)
      row.addEventListener('mouseleave', handleMultipleLeaveTableHover)
    })

    return () => {
      tableBody && tableBody.removeEventListener('scroll', handleMultipleTableScroll)
      rows.forEach(row => {
        row.removeEventListener('mouseover', handleMultipleTableHover)
        row.removeEventListener('mouseleave', handleMultipleLeaveTableHover)
      })
    }
  }, [articleSelected])

  useEffect(() => {
    setComponentWidth(
      (priceListingTableRef?.current?.querySelector('.ant-table-container .ant-table-thead') as HTMLElement)
        ?.offsetWidth + 1,
    )
  }, [articleSelected, props.activeListing, cellPriceWidth])

  return (
    <div className='' key={'listing-table-wrap-' + props.activeListing} ref={priceListingTableRef}>
      <animated.div
        style={useSpring({
          width: isColumnOpen ? componentWidth : 30,
          onRest: () => {
            setIsColumnMinified(!isColumnOpen)
          },
        })}
        className={'listing multi-table-slide-container' + (isColumnOpen ? ' open' : ' closed')}
      >
        <Table
          className={'listing-table multi-table same-table-scroll'}
          columns={listingTableColumns}
          dataSource={listingPriceMappedData}
          pagination={false}
          tableLayout='fixed'
          scroll={{ y: priceStandardHeight, x: 'auto' }}
        />
      </animated.div>
    </div>
  )
}

export default Prices
